<template>
    <app-layout v-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>
            </common-content-spacer>

            <referrals-confirmation
                v-if="isProposedTimeSent"
                :heading="$t('pages.referrals.new.steps.confirmation.heading')"
                type="success"
            >
                <template #content>
                    <common-content-spacer height="small">
                        <common-page-header
                            :heading="$t('pages.referrals.pool.selected_slots')"
                            size="small"
                            contentSpacing="tiny"
                        ></common-page-header>
                        <referral-time-slots-preview :groupSlotsByDate="selectedSlotsByDate"></referral-time-slots-preview>
                        <common-buttons>
                            <common-button
                                :label="$t('pages.auth.signup.steps.congratulations.content.button.proceed_to_dashboard')"
                                size="large"
                                type="primary"
                                @click="$router.push({ name: 'index.home' })"
                            ></common-button>
                        </common-buttons>
                    </common-content-spacer>
                </template>
            </referrals-confirmation>

            <common-page-split v-else :reverse="true">
                <template #aside>
                    <common-content-spacer height="tiny">
                        <practice-member-card
                            v-if="physician && !isTablet"
                            :physician="physician"
                            :isStandalone="false"
                        ></practice-member-card>
                        <common-content-group>
                            <common-content-spacer height="tiny">
                                <common-page-header
                                    :heading="$t('pages.referrals.pool.selected_slots')"
                                    size="small"
                                    contentSpacing="medium"
                                ></common-page-header>
                                <referral-time-slots-preview
                                    :groupSlotsByDate="selectedSlotsByDate"
                                    :showDeleteIcon="true"
                                    @remove-item="onRemoveItem"
                                ></referral-time-slots-preview>

                                <common-buttons>
                                    <common-button
                                        :label="$t('pages.referrals.pool.send_to_patient_provider')"
                                        :isSubmittable="false"
                                        :isDisabled="sharedState.totalSlotCount < 3"
                                        size="medium"
                                        type="primary"
                                        :isProcessing="isSending"
                                        @click="sendToPatient()"
                                    ></common-button>
                                </common-buttons>
                            </common-content-spacer>
                        </common-content-group>
                    </common-content-spacer>
                </template>

                <template #main>
                    <common-content-wrapper width="huge">
                        <referrals-new-schedule
                            ref="referralsNewSchedule"
                            :physician="physician"
                            :practice="{}"
                            :location="{}"
                            :selectedSlotsByDate="selectedSlotsByDate"
                            @selectSlot="slotChanged($event)"
                        ></referrals-new-schedule>
                    </common-content-wrapper>
                </template>
            </common-page-split>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import ReferralsNewSchedule from 'components/page-elements/referrals/ReferralsNewSchedule'
    import PracticeMemberCard from 'components/page-elements/practice/PracticeMemberCard'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralsNewPageMixin from 'mixins/ReferralsNewPageMixin'
    import dayjs from 'dayjs'
    import emitter from 'tiny-emitter/instance'
    import UsersAPI from 'api/UsersAPI'
    import ReferralTimeSlotsPreview from 'components/page-elements/referrals/ReferralTimeSlotsPreview.vue'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ReferralsNewPageMixin, ScreenSizeDetectionMixin],
        components: {
            AppLayout,
            ReferralsNewSchedule,
            PracticeMemberCard,
            ReferralTimeSlotsPreview,
        },

        data() {
            return {
                physician: {},
                selectedSlotsByDate: {},
                sharedState: {
                    totalSlotCount: 0,
                },
                isSending: false,
                isProposedTimeSent: false,
            }
        },

        provide() {
            return {
                isReferralPool: true,
                sharedState: this.sharedState,
            }
        },

        loadData(self) {
            return {
                physician: UsersAPI.get(self.$route.query.fromUserId),
            }
        },

        computed: {
            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.referrals.pool.breadcrumbs.referrals'),
                        route: { name: `pool.received` },
                    },
                    {
                        label: this.$t(`pages.referrals.index.breadcrumbs.received`),
                        route: { name: `pool.received` },
                    },
                    {
                        label: this.$t('pages.referrals.pool.current_referral'),
                        route: { name: `pool.received.show`, params: { id: this.$route.params.poolId } },
                    },
                    {
                        label: this.$t('pages.referrals.pool.propose_time'),
                    },
                ]
            },
        },

        methods: {
            slotChanged(payload) {
                const date = payload.date
                const updatedSlots = payload.slots.map((slot) => ({
                    label: slot.label,
                    date: slot.value.date,
                    startTime: slot.value.startTime,
                    endTime: slot.value.endTime,
                    from: slot.value.from,
                    to: slot.value.to,
                }))

                updatedSlots.sort((a, b) => {
                    const dateA = dayjs(`${a.date} ${a.startTime}`)
                    const dateB = dayjs(`${b.date} ${b.startTime}`)
                    return dateA - dateB
                })
                this.$set(this.selectedSlotsByDate, date, updatedSlots)
                const sortedSlotsByDate = {}
                Object.keys(this.selectedSlotsByDate)
                    .sort((a, b) => dayjs(a).unix() - dayjs(b).unix())
                    .forEach((key) => {
                        sortedSlotsByDate[key] = this.selectedSlotsByDate[key]
                    })

                this.selectedSlotsByDate = sortedSlotsByDate
                this.updateSlotCount()
            },

            formatDate(dateStr) {
                return dayjs(dateStr).format('DD MMMM YYYY')
            },
            formatTime(timeStr) {
                return dayjs(timeStr, 'HH:mm').format('hh:mm A')
            },

            updateSlotCount() {
                this.sharedState.totalSlotCount = Object.values(this.selectedSlotsByDate).reduce(
                    (total, slots) => total + slots.length,
                    0,
                )
            },

            onRemoveItem(payload) {
                this.removeItem(payload.slot, payload.date)
            },

            removeItem(slot, date) {
                const slotsForDate = this.selectedSlotsByDate[date]
                const updatedSlots = slotsForDate.filter((s) => s.label !== slot.label)

                if (updatedSlots.length) {
                    this.$set(this.selectedSlotsByDate, date, updatedSlots)
                } else {
                    this.$delete(this.selectedSlotsByDate, date)
                }

                this.updateSlotCount()
                emitter.emit('slot-removed', { slot: slot, date: date })
            },

            sendToPatient() {
                const proposedTimeSlots = []
                Object.values(this.selectedSlotsByDate).forEach((slots) => {
                    slots.forEach((slot) => {
                        proposedTimeSlots.push({
                            date: slot.date,
                            startTime: slot.startTime,
                            endTime: slot.endTime,
                        })
                    })
                })

                const request = {
                    practiceLocationId: Number(this.$route.params.locationId),
                    proposedTimeSlots: proposedTimeSlots,
                    distance: this.$route.query.distance,
                }
                this.isSending = true
                ReferralsPoolAPI.sendProposeTimeSlots(this.$route.params.poolId, request)
                    .then(() => {
                        this.isProposedTimeSent = true
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },
        },
    }
</script>
